import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Download, Play, History, Calendar, Layout, FileText, Database } from 'lucide-react';
import { API_URL } from './constants';
import { useUserDetails } from "./UserDetailsContext";
import { useNavigate } from 'react-router-dom';

const EntryRow = ({ entry }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { userDetails, setUserDetails, token } = useUserDetails();
  const navigate = useNavigate();

  const {
    prompt,
    purpose,
    timestamp,
    language,
    nowOrLater,
    col_names,
    number,
    filename,
    download_url
  } = entry;

  // Check if user has enough credits to download
  const hasEnoughCredits = true;

  const handleDownload = async (e) => {
    e.stopPropagation(); // Prevent expansion
    if (!hasEnoughCredits || isDownloading) return;

    try {
      setIsDownloading(true);
      const response = await fetch(`${API_URL}/api/request-download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          data: "",
          userId: userDetails.user_id,
          email: userDetails.email,
          filename: download_url,
          credits: 0
        })
      });

      if (response.ok) {
        const link = document.createElement('a');
        link.href = download_url;
        link.download = filename || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download tracking failed:', error);
      alert('Failed to process download. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const [isLoadingContent, setIsLoadingContent] = useState(false);

  const handleLoadIntoGrid = async (e) => {
    e.stopPropagation();
    
    if (!download_url) {
      navigate('/clean', { state: { activity: entry } });
      return;
    }

    try {
      setIsLoadingContent(true);
      const response = await fetch(`${API_URL}/api/file-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ filename: filename })
      });
      
      if (!response.ok) throw new Error('Failed to fetch dataset content');
      
      const { content } = await response.json();
      let parsedData = [];
      let parsedHeaders = [];

      // Determine format from filename
      const lowerFile = filename.toLowerCase();
      if (lowerFile.endsWith('.json') || lowerFile.endsWith('.jsonl')) {
        try {
          if (lowerFile.endsWith('.jsonl')) {
             parsedData = content.split('\n')
               .filter(line => line.trim())
               .map(line => JSON.parse(line));
          } else {
             parsedData = JSON.parse(content);
             if (!Array.isArray(parsedData)) parsedData = [parsedData];
          }
          
          if (parsedData.length > 0) {
            parsedHeaders = Object.keys(parsedData[0]);
          }
        } catch (e) {
          console.error('JSON parse error:', e);
        }
      } else {
        // Assume CSV
        const Papa = require('papaparse');
        const results = Papa.parse(content, { header: true, skipEmptyLines: true });
        parsedData = results.data;
        parsedHeaders = results.meta.fields;
      }

      navigate('/clean', { 
        state: { 
          activity: entry,
          loadedData: parsedData,
          loadedHeaders: parsedHeaders
        } 
      });
    } catch (err) {
      console.error('Error loading content:', err);
      // Fallback to just loading settings if content fetch fails
      navigate('/clean', { state: { activity: entry } });
    } finally {
      setIsLoadingContent(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className={`border-b border-gray-100 transition-all duration-300 ${isExpanded ? 'bg-blue-50/30' : 'hover:bg-gray-50'}`}>
      <div
        className="py-5 px-6 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600 transition-transform duration-300"
            style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
            <ChevronRight size={18} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full items-center">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-1">
                <FileText size={14} className="text-blue-500" />
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">Prompt Preview</span>
              </div>
              <div className="text-sm font-medium text-gray-800 truncate max-w-md">
                {prompt || 'No content'}
              </div>
            </div>

            <div className="hidden md:block">
              <div className="flex items-center gap-2 mb-1">
                <Calendar size={14} className="text-gray-400" />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Date</span>
              </div>
              <div className="text-sm text-gray-600">
                {formatDate(timestamp)}
              </div>
            </div>

            <div className="flex justify-end gap-2">
               <button
                onClick={handleLoadIntoGrid}
                disabled={isLoadingContent}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold transition-all shadow-sm hover:shadow active:scale-95 rounded-lg ${
                  isLoadingContent ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                title="Load this prompt back into the workspace"
              >
                <Play size={12} fill="currentColor" className={isLoadingContent ? 'animate-pulse' : ''} />
                {isLoadingContent ? 'Loading...' : 'Load to Grid'}
              </button>

              {/* download_url && (
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold bg-white text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all shadow-sm active:scale-95 disabled:opacity-50"
                  title="Download raw file"
                >
                  <Download size={14} />
                  {isDownloading ? '...' : 'Export'}
                </button>
              ) */}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-5 ml-12 p-6 bg-white rounded-xl border border-blue-100 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                    <FileText size={14} /> Full Instruction Set
                  </h4>
                  <div className="p-4 bg-gray-50 rounded-xl text-sm text-gray-700 leading-relaxed whitespace-pre-wrap border border-gray-100">
                    {prompt || 'No prompt provided.'}
                  </div>
                </div>

                {col_names && (
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                      <Layout size={14} /> Schema / Columns
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {col_names.split(/[,|]/).map((col, i) => (
                        <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-[11px] font-bold rounded-md border border-blue-100 uppercase tracking-tight">
                          {col.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3">Generation Specs</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-xs text-gray-500 font-medium">Rows Count</span>
                      <span className="text-sm font-bold text-gray-700">{number || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-xs text-gray-500 font-medium">Language</span>
                      <span className="text-sm font-bold text-blue-600 uppercase tracking-tighter">{language || 'English'}</span>
                    </div>
                  </div>
                </div>

                {/* <div>
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3">Original Intent</h4>
                  <p className="text-sm text-gray-600 italic">"{purpose || 'No purpose stated.'}"</p>
                </div> */}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="divide-y divide-gray-100">
    {[1, 2, 3, 4, 5].map(i => (
      <div key={i} className="py-6 px-8 animate-pulse">
        <div className="flex gap-4">
          <div className="w-8 h-8 bg-gray-100 rounded-full"></div>
          <div className="flex-1 space-y-3">
            <div className="h-4 bg-gray-100 rounded w-3/4"></div>
            <div className="h-3 bg-gray-50 rounded w-1/4"></div>
          </div>
          <div className="w-24 h-8 bg-gray-100 rounded-lg"></div>
        </div>
      </div>
    ))}
  </div>
);

const EntryTable = ({ user }) => {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userDetails, setUserDetails, token } = useUserDetails();

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`${API_URL}/api/user-activity`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ "user_id": user.uid })
        });

        if (!response.ok) throw new Error('Could not fetch your activity history.');
        const data = await response.json();

        // Sort by timestamp descending
        const sortedData = (data["user_activities"] || []).sort((a, b) =>
          new Date(b.timestamp) - new Date(a.timestamp)
        );

        setEntries(sortedData);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (user && token) {
      fetchEntries();
    }
  }, [user, token]);

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-10 font-inter">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
              <History className="text-blue-600" size={32} />
              Data History
            </h1>
            <p className="text-gray-500 mt-2 font-medium">Manage and reload your past data generation sessions.</p>
          </div>
          <div className="flex items-center gap-3 px-4 py-2 bg-blue-50 rounded-xl border border-blue-100">
            <Database className="text-blue-600" size={20} />
            <div className="text-left">
              <div className="text-[10px] font-black text-blue-400 uppercase tracking-wider leading-none">Total Activity</div>
              <div className="text-lg font-bold text-blue-700 leading-none mt-1">{entries.length} Sessions</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden min-h-[400px]">
          {isLoading ? (
            <LoadingSkeleton />
          ) : error ? (
            <div className="flex flex-col items-center justify-center p-20 text-center">
              <div className="bg-red-50 text-red-500 p-4 rounded-full mb-4">
                <ChevronDown size={32} className="rotate-180" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Something went wrong</h3>
              <p className="text-gray-500 mt-1">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-6 px-5 py-2 bg-gray-900 text-white rounded-xl text-sm font-bold"
              >
                Retry Loading
              </button>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-20 text-center">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                <Database className="text-gray-200" size={40} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">No data magic yet</h3>
              <p className="text-gray-500 mt-2 max-w-xs mx-auto">
                Starts creating synthetic datasets and your history will appear right here!
              </p>
              <button
                onClick={() => window.location.href = '/'}
                className="mt-8 px-8 py-3 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-600/20 font-black text-sm uppercase tracking-wider"
              >
                Generate First Dataset
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {entries.map((entry, index) => (
                <EntryRow key={index} entry={entry} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntryTable;